import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';
import useOptionalLocationSearch from 'src/hooks/useOptionalLocationSearch';
// @mui
import { Box, Chip, Typography, Table, TableBody, TableContainer, TableCell, TableRow, MenuItem, Dialog, DialogTitle, DialogActions, Button, Zoom, Tooltip } from '@mui/material';
// other libraries
import moment from 'moment';
import useSnackbar from 'src/hooks/useCustomSnackbar';
import DemoBlur, { piiTitle } from 'src/components/DemoBlur';
import CalcomAppointmentBadge from '../calcom/CalcomAppointmentBadge';
import { TableHeadCustom, TableMoreMenu } from '../table';
import Iconify from '../Iconify';
import { OpenReSchedulePopup, OpenAttachmentsPopup } from '../fullScreenPopUps';
import CustomTableCell from './CustomTableCell';
import NoAppointmentComponent from '../NoAppointmentComponent';
import AppointmentDepositBadge from '../appointments/AppointmentDepositBadge';
import { useSelector, dispatch } from '../../redux/store';
import { appointmentsAttended } from '../../redux/slices/lead';
import { useTable, useToggle } from '../../hooks';
import useAppointmentCancellation from '../../hooks/useAppointmentCancellation';
import { upcomingTableLabels } from '../../constants/LeadDetailConst';
import { getAssignees } from '../../utils/multiAsignees';
import { capitalizeFirstWord } from '../../utils/capitalizedFunc';
import { getIcon, StatusValue } from '../../utils/appointmentUtils';
import { AppointmentCancellationDialog, AppointmentCancellationMenuItem } from '../AppointmentCancellationControls';
import DentallyAppointmentBadge from '../dentally/DentallyAppointmentBadge';
import { cancelBtnStyle, yesBtnStyle, dateTypoTextStyle, timeTypoTextStyle, methodTypoTextStyle, treatmentTypoStyle, statusTypoTextStyle, dialogTitle, dialogActions, normalWhiteSpace } from '../../styles/UpcommingAppointments';
import { toAssigneeIds } from '../../utils/toAssigneeIds';
import { consoleUrl } from '../pms/video/consoleUrl';
import { appointmentStartIso, isWithinJoinWindow } from '../pms/video/joinWindow';

const UpcommingAppointments = ({ leadAppointment, treatmentData, leadDetail, handleUpdate }) => {
    const { practiceId } = useSelector((state) => state.practiceTreatmentList);
    // Deep link from the hero next-booking chip: ?appointmentId= highlights that exact row.
    const highlightedAppointmentId = new URLSearchParams(useOptionalLocationSearch()).get('appointmentId');
    const [tableData, setTableData] = useState(leadAppointment);
    const { practiceUser } = useSelector((state) => state.practiceUser);
    const [openMenu, setOpenMenuActions] = useState(null);
    const rowData = [];
    const upcomingRef = useRef();
    const [appoinmentID, setAppoinmentID] = useState(null);
    const [appoinmentSelectData, setAppoinmentSelectData] = useState(null);
    const { enqueueSnackbar } = useSnackbar();
    const inputRef = useRef();
    const [confirmation, setConfirmation] = useState(false);
    const cancellation = useAppointmentCancellation({
        practiceId,
        leadId: leadDetail?.id,
        appointments: leadAppointment,
    });

    useEffect(() => {
        if (leadAppointment) {
            leadAppointment?.map((item) => {
                const latestData = item;
                rowData.push({
                    assignees: toAssigneeIds(item?.assignees),
                    Date: moment(latestData?.datetime, 'DD-MM-YYYY').format('ddd, D MMM YYYY'),
                    Time: moment(latestData?.datetime, 'DD-MM-YYYY HH:mm:ss').format('hh:mm A'),
                    Treatment: treatmentData?.treatment,
                    method: latestData?.method,
                    Practioner: latestData?.assignees?.length > 0 ? practiceUser?.results?.filter(el => latestData?.assignees?.some((ele) => ele === el.id) || latestData?.assignees?.some((ele) => ele?.id === el.id)) : [],
                    Status: latestData?.status,
                    appointment_id: latestData?.id,
                    dentally: latestData?.dentally || null,
                    calcom: latestData?.calcom || null,
                    datetime: latestData?.datetime,
                    contacted_via: latestData?.contacted_via,
                    type: latestData?.type || '',
                    note: latestData?.note || '',
                    depositSummary: latestData?.deposit_summary || null,
                    duration: latestData?.duration ?? latestData?.duration_minutes ?? null,
                });
                return item;
            });
            setTableData(rowData);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [leadAppointment]);

    const { onSelectAllRows } = useTable();

    const handleOpenMenu = (event, row) => {
        setOpenMenuActions(event.currentTarget);
        upcomingRef.current = row?.appointment_id;
    };

    const handleCloseMenu = () => setOpenMenuActions(null);

    const handleReScheduleClose = () => onCloseReSchedule();

    const handleAttachmentClose = () => onCloseAttachment();

    const {
        toggle: OpenReSchedule,
        onOpen: onOpenReSchedule,
        onClose: onCloseReSchedule
    } = useToggle();

    const {
        toggle: OpenAttachments,
        onOpen: onOpenAttachment,
        onClose: onCloseAttachment
    } = useToggle();

    useEffect(() => {
        const htmlElement = document.querySelector('body');
        if (OpenAttachments) {
            htmlElement.classList.add('overflow-hidden');
        } else {
            htmlElement.classList.remove('overflow-hidden');
        }
    }, [OpenAttachments]);

    const handlePopup = (appointmentId, appointmentData) => {
        setAppoinmentID(appointmentId);
        setAppoinmentSelectData(appointmentData);
        setTimeout(() => {
            onOpenReSchedule();
            setOpenMenuActions(null);
        }, 800);
    };

    const attachmentPopup = (appointmentId, appointmentData) => {
        setAppoinmentID(appointmentId);
        setAppoinmentSelectData(appointmentData);
        setTimeout(() => {
            onOpenAttachment();
            setOpenMenuActions(null);
        }, 800);
    };

    const handleAttended = (appointmentData) => {
        setAppoinmentSelectData(appointmentData);
        setConfirmation(true);
        setOpenMenuActions(null);
    };

    const attendedTreatmentAPI = () => {
        const appointmentDetail = {
            contacted_via: appoinmentSelectData?.contacted_via || '',
            datetime: appoinmentSelectData?.datetime,
            method: appoinmentSelectData.method,
            note: appoinmentSelectData.note,
        };
        dispatch(appointmentsAttended(practiceId, leadDetail?.id, appoinmentSelectData?.appointment_id, appointmentDetail, handleStatusUpdate));
    };

    const handleStatusUpdate = (data) => {
        enqueueSnackbar('Lead treatment status updated successfully', {
            variant: data.success ? 'success' : 'error',
            anchorOrigin: {
                vertical: 'top',
                horizontal: 'center'
            }
        });
        setConfirmation(false);
        handleUpdate();
    };

    const customIcon = ({ title, icon }) => <Tooltip title={title} placement='bottom' TransitionComponent={Zoom} TransitionProps={{ timeout: 300 }}>
        <Iconify icon={icon} width={18} height={18} />
    </Tooltip>;

    // One clock reading per render drives every row's join window, so two rows
    // can never disagree about what time it is.
    const now = new Date();

    return (
        <Box>
            {leadAppointment && leadAppointment?.length > 0 ?
                <TableContainer sx={{ width: '100%', position: 'relative' }}>
                    <Table>
                        <TableHeadCustom
                            headLabel={upcomingTableLabels}
                            rowCount={tableData?.length}
                            sx={{ ' .MuiTableCell-head': { boxShadow: 'none !important', minWidth: 'auto' } }}
                            onSelectAllRows={(checked) => onSelectAllRows(checked)}
                        />
                        <TableBody >
                            {tableData !== undefined && tableData?.map((row, index) => (
                                <TableRow
                                    key={index}
                                    selected={String(row.appointment_id) === highlightedAppointmentId}
                                    data-appointment-id={row.appointment_id}
                                    sx={{ borderBottom: '1px solid rgba(145, 158, 171, 0.12)' }}
                                >
                                    <CustomTableCell label={row?.Date} style={dateTypoTextStyle} />
                                    <CustomTableCell label={row?.Time} style={timeTypoTextStyle} />
                                    <TableCell>
                                        <Box sx={{ ml: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
                                            <Typography variant='span' noWrap sx={treatmentTypoStyle}>{row?.Treatment}</Typography>
                                            <AppointmentDepositBadge depositSummary={row?.depositSummary} />
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ ml: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
                                            <Typography variant='span' noWrap sx={methodTypoTextStyle}>
                                                {getIcon(row, customIcon)}
                                            </Typography>
                                            {row?.method === 'VIDEO_CALL' && <Chip size='small' icon={<Iconify icon='eva:video-fill' />} label='Video' />}
                                            {row?.method === 'VIDEO_CALL' && isWithinJoinWindow(row?.datetime, row?.duration, now) &&
                                                <Button
                                                    size='small'
                                                    variant='contained'
                                                    component={RouterLink}
                                                    to={consoleUrl(row?.appointment_id, { leadId: leadDetail?.id, startAt: appointmentStartIso(row?.datetime) })}
                                                    sx={{ textTransform: 'none' }}
                                                >
                                                    Join video
                                                </Button>
                                            }
                                        </Box>
                                    </TableCell>
                                    <CustomTableCell label={capitalizeFirstWord(row?.type)} style={methodTypoTextStyle} />
                                    <TableCell>
                                        <Box sx={{ ml: 0 }}>
                                            <Typography variant='span' noWrap sx={statusTypoTextStyle}>
                                                <DemoBlur>
<Tooltip title={piiTitle(getAssignees({ assignees: row?.Practioner }))} placement='bottom' TransitionComponent={Zoom} TransitionProps={{ timeout: 300 }}>
                                                    <span style={{ textTransform: 'capitalize', marginRight: row?.Practioner?.[0]?.user?.salutation ? '4px' : '0' }}>{`${row?.Practioner?.[0]?.user?.salutation || ''}`.toLowerCase()}</span>{row.Practioner?.[0]?.user?.first_name} {row.Practioner?.[0]?.user?.last_name} {row?.Practioner?.length > 1 ? `+ ${row?.Practioner?.length - 1}` : ''}
                                                </Tooltip>
</DemoBlur>
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ ml: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
                                            <Typography variant='span' noWrap sx={statusTypoTextStyle}>
                                                {StatusValue(row?.Status)}
                                            </Typography>
                                            {/* Provider identity / creation-cancellation state available today; not a sync verdict. */}
                                            {!row.calcom && <DentallyAppointmentBadge dentally={row?.dentally} />}
                                            <CalcomAppointmentBadge calcom={row.calcom} practiceId={practiceId} leadId={leadDetail?.id} appointmentId={row.appointment_id} />
                                        </Box>
                                    </TableCell>
                                    <TableCell align='right' >
                                        {row.Status !== 'CANCELLED' && <TableMoreMenu
                                            open={upcomingRef.current === row.appointment_id && openMenu}
                                            sx={{ width: '180px' }}
                                            onOpen={(e) => handleOpenMenu(e, row)}
                                            onClose={handleCloseMenu}
                                            actions={
                                                <Box>
                                                    {row.Status !== 'CANCELLED' &&
                                                        <MenuItem disabled={Boolean(row.calcom && !row.calcom.can_reschedule)} onClick={() => handlePopup(row.appointment_id, row)} sx={normalWhiteSpace}>
                                                            Edit/Reschedule
                                                        </MenuItem>
                                                    }
                                                    <AppointmentCancellationMenuItem controller={cancellation} row={row} />
                                                    {row?.Status === 'BOOKED' &&
                                                        <MenuItem onClick={() => handleAttended(row)} sx={normalWhiteSpace}>
                                                            Attended
                                                        </MenuItem>
                                                    }
                                                    <MenuItem onClick={() => attachmentPopup(row.appointment_id, row)} sx={normalWhiteSpace}>
                                                        View detail
                                                    </MenuItem>
                                                </Box>
                                            }
                                        />}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                : <NoAppointmentComponent />
            }
            <Dialog open={confirmation} onClose={() => setConfirmation(false)}>
                <DialogTitle sx={dialogTitle}>
                    Are you sure you want to attended the treatment?
                </DialogTitle>
                <DialogActions sx={dialogActions}>
                    <Button sx={cancelBtnStyle} onClick={() => setConfirmation(false)}>
                        Cancel
                    </Button>
                    <Button type='submit' sx={yesBtnStyle} onClick={() => attendedTreatmentAPI()}>
                        Yes
                    </Button>
                </DialogActions>
            </Dialog>
            <AppointmentCancellationDialog controller={cancellation} />
            {OpenReSchedule && <OpenReSchedulePopup
                open={OpenReSchedule}
                headerButtons
                onClose={handleReScheduleClose}
                treatmentData={treatmentData}
                inputRef={inputRef}
                appoinmentID={appoinmentID}
                appoinmentSelectData={appoinmentSelectData}
                leadDetail={leadDetail}
            />}
            {OpenAttachments && <OpenAttachmentsPopup
                open={OpenAttachments}
                headerButtons
                onClose={handleAttachmentClose}
                inputRef={inputRef}
                appoinmentID={appoinmentID}
            />}
        </Box>
    );
};

UpcommingAppointments.propTypes = {
    leadAppointment: PropTypes.array,
    treatmentData: PropTypes.object,
    leadDetail: PropTypes.object,
    handleUpdate: PropTypes.func,
};

export default UpcommingAppointments;
